const YOTPO_STOREFRONT_ENDPOINT = 'https://api-cdn.yotpo.com/v3/storefront/store/';
const YOTPO_LOADER_KEY_PATTERN = /\/v1\/loader\/([A-Za-z0-9_-]+)/;
const FYGG_RATING_SVG_NAMESPACE = 'http://www.w3.org/2000/svg';
const FYGG_RATING_STAR_WHOLE_PATH =
  'M10 1 12.12 7.09 18.56 7.22 13.42 11.11 15.29 17.28 10 13.6 4.71 17.28 6.58 11.11 1.44 7.22 7.88 7.09Z';
const FYGG_RATING_STAR_HALF_PATH = 'M10 1 7.88 7.09 1.44 7.22 6.58 11.11 4.71 17.28 10 13.6Z';
const FYGG_RATING_PENDING_ATTRIBUTE = 'data-fygg-rating-pending';

const fyggRatingBottomlineCache = new Map();
let fyggRatingYotpoAppKey = null;
let fyggRatingSyncQueued = false;

const resolveYotpoAppKey = () => {
  if (fyggRatingYotpoAppKey) return fyggRatingYotpoAppKey;

  if (typeof window.FYGG_YOTPO_APP_KEY === 'string' && window.FYGG_YOTPO_APP_KEY) {
    fyggRatingYotpoAppKey = window.FYGG_YOTPO_APP_KEY;
    return fyggRatingYotpoAppKey;
  }

  for (const script of document.querySelectorAll('script[src*="/v1/loader/"]')) {
    const match = YOTPO_LOADER_KEY_PATTERN.exec(script.getAttribute('src') ?? '');
    if (match) {
      fyggRatingYotpoAppKey = match[1];
      return fyggRatingYotpoAppKey;
    }
  }

  return null;
};

const requestYotpoBottomline = (productId) => {
  const cached = fyggRatingBottomlineCache.get(productId);
  if (cached) return cached;

  const appKey = resolveYotpoAppKey();
  if (!appKey) return null;

  const url = `${YOTPO_STOREFRONT_ENDPOINT}${encodeURIComponent(appKey)}/product/${encodeURIComponent(productId)}/reviews?perPage=1`;
  const bottomlineRequest = fetch(url, { credentials: 'omit' })
    .then((response) => (response.ok ? response.json() : null))
    .then((body) => {
      const bottomline = body?.bottomline;
      const score = Number.parseFloat(bottomline?.averageScore);
      const count = Number.parseInt(bottomline?.totalReview, 10);
      return score > 0 && count > 0 ? { score, count } : null;
    })
    .catch(() => null);

  fyggRatingBottomlineCache.set(productId, bottomlineRequest);
  return bottomlineRequest;
};

const createFyggRatingStarPath = (className, pathData) => {
  const path = document.createElementNS(FYGG_RATING_SVG_NAMESPACE, 'path');
  path.setAttribute('class', className);
  path.setAttribute('d', pathData);
  return path;
};

const buildFyggRatingStarPaths = (blockClass, starState) => {
  if (starState === 'full') {
    return [createFyggRatingStarPath(`${blockClass}__star-on`, FYGG_RATING_STAR_WHOLE_PATH)];
  }
  if (starState === 'half') {
    return [
      createFyggRatingStarPath(`${blockClass}__star-off`, FYGG_RATING_STAR_WHOLE_PATH),
      createFyggRatingStarPath(`${blockClass}__star-on`, FYGG_RATING_STAR_HALF_PATH)
    ];
  }
  return [createFyggRatingStarPath(`${blockClass}__star-off`, FYGG_RATING_STAR_WHOLE_PATH)];
};

const paintFyggRatingStar = (starSvg, starState) => {
  const blockClass = (starSvg.getAttribute('class') ?? '').split(/\s+/)[0].replace(/__star$/, '');
  if (blockClass) starSvg.replaceChildren(...buildFyggRatingStarPaths(blockClass, starState));
};

const fyggRatingStarState = (halfSteps, starIndex) => {
  const fullAt = (starIndex + 1) * 2;
  if (halfSteps >= fullAt) return 'full';
  return halfSteps === fullAt - 1 ? 'half' : 'empty';
};

const drawFyggRatingStars = (starsWrapper, score) => {
  const starSvgs = starsWrapper.querySelectorAll('svg');
  if (starSvgs.length !== 5) return;

  const halfSteps = Math.min(10, Math.round(score * 2));
  starSvgs.forEach((starSvg, starIndex) =>
    paintFyggRatingStar(starSvg, fyggRatingStarState(halfSteps, starIndex))
  );
};

const fillFyggRatingSlots = (ratingElement, slotSelector, text) => {
  for (const slot of ratingElement.querySelectorAll(slotSelector)) {
    if (slot.textContent !== text) slot.textContent = text;
  }
};

const applyFyggRating = (ratingElement, { score, count }) => {
  const stamp = `${ratingElement.getAttribute('data-fygg-rating')}|${score}|${count}`;
  if (ratingElement.dataset.fyggRatingLive === stamp) return;

  const displayScore = score.toFixed(1);
  const reviewNoun = count === 1 ? 'review' : 'reviews';

  const starsWrapper = ratingElement.querySelector('[data-fygg-rating-stars]');
  if (starsWrapper) drawFyggRatingStars(starsWrapper, score);

  fillFyggRatingSlots(ratingElement, '[data-fygg-rating-score]', displayScore);
  fillFyggRatingSlots(ratingElement, '[data-fygg-rating-count]', `${count} ${reviewNoun}`);
  fillFyggRatingSlots(ratingElement, '[data-fygg-rating-count-compact]', `(${count})`);

  const labelledElement = ratingElement.hasAttribute('data-fygg-rating-label')
    ? ratingElement
    : ratingElement.querySelector('[data-fygg-rating-label]');

  if (labelledElement) {
    const label = labelledElement
      .getAttribute('data-fygg-rating-label')
      .replace('{score}', displayScore)
      .replace('{count}', count)
      .replace('{reviews}', reviewNoun);
    labelledElement.setAttribute('aria-label', label);
  }

  ratingElement.removeAttribute('data-fygg-rating-empty');
  ratingElement.dataset.fyggRatingLive = stamp;
};

const syncFyggRatings = () => {
  for (const ratingElement of document.querySelectorAll('[data-fygg-rating]')) {
    const productId = ratingElement.getAttribute('data-fygg-rating');
    if (!productId) continue;

    const bottomlineRequest = requestYotpoBottomline(productId);
    if (!bottomlineRequest) continue;

    bottomlineRequest.then((bottomline) => {
      if (bottomline) applyFyggRating(ratingElement, bottomline);
      ratingElement.removeAttribute(FYGG_RATING_PENDING_ATTRIBUTE);
    });
  }
};

const scheduleFyggRatingSync = () => {
  if (fyggRatingSyncQueued) return;
  fyggRatingSyncQueued = true;
  requestAnimationFrame(() => {
    fyggRatingSyncQueued = false;
    syncFyggRatings();
  });
};

const startFyggRatingSync = () => {
  syncFyggRatings();
  new MutationObserver(scheduleFyggRatingSync).observe(document.documentElement, {
    childList: true,
    subtree: true
  });
  setTimeout(syncFyggRatings, 800);
  setTimeout(syncFyggRatings, 2500);
};

window.fyggRatingLive = { sync: syncFyggRatings };

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', startFyggRatingSync, { once: true });
} else {
  startFyggRatingSync();
}
